import { DrawStatus } from "@/enums/DrawStatus";
import { ProjectRole } from "@/enums/ProjectRole";
import { z } from "zod";
import { CodeSchema } from "./Codes";
import { CheckingAccountSchema } from "./CheckingAccount";
import { EntitySchema } from "./Entity";
import { SelectedDrawSchema } from "./Draw";
import { DrawItemListSchema, PortalDrawItemSchema } from "./DrawItem";
import { SubdivisionSchema } from "./Subdivision";
import { ProjectReminderListSchema } from "./Reminder";
import { ProjectEntityListSchema } from "./ProjectEntity";
import { FlexRowFieldListSchema } from "./FlexRows";
import { ChecklistListSchema } from "./Checklist";
import { UserPermissionsSchema } from "./User";
import { ActivityLogInfoSchema } from "./ActivityLog";
import { ArchivedProjectsSchema } from "./ArchivedProject";
import { ProjectStatusSchema } from "./ProjectStatus";

export const ProjectSummarySchema = z.object({
  projectId: z.string(),
  projectCode: z.string(),
  projectName: z.string(),
  projectStatus: ProjectStatusSchema,
  propertyAddress: z.string(),
  propertyCity: z.string(),
  propertyState: z.string().nullable(),
  propertyZip: z.string(),
  propertyCounty: z.string(),
  currentDraw: z.number(),
  budget: z.number(),
  drawnToDate: z.number(),
  fundsAvailable: z.number(),
  drawStatus: z.nativeEnum(DrawStatus),
  locked: z.boolean(),
  lockedBy: z.string().nullable(),
  imported: z.boolean(),
  hasNewDraw: z.boolean(),
  hasImportedDraws: z.boolean(),
  hasImportedInspections: z.boolean(),
  hasNewPortalFiles: z.boolean(),
  entityType: z.nativeEnum(ProjectRole).nullable(),
  lastActivity: z.coerce.date().nullable(),
  lastUpdatedBy: z.string(),
});

const ProjectSummaryAdminSchema = z.object({
  userGroup: z.string(),
  projectType: z.string(),
  propertyType: z.string(),
  processor: z.string(),
  borrower: z.string(),
  builder: z.string(),
  inspector: z.string(),
});

const ProjectSummaryPortalSchema = z.object({
  userGroup: z.string().nullable(),
  projectType: z.string().nullable(),
  propertyType: z.string().nullable(),
  processor: z.string().nullable(),
  borrower: z.string().nullable(),
  builder: z.string().nullable(),
  inspector: z.string().nullable(),
});

export const AdminProjectSummary = z.intersection(ProjectSummarySchema, ProjectSummaryAdminSchema);

export const ProjectSummaryAdminListSchema = z.object({
  projects: z.array(AdminProjectSummary),
  archived: z.array(ArchivedProjectsSchema),
});
export const ProjectSummaryPortalListSchema = z.array(z.intersection(ProjectSummarySchema, ProjectSummaryPortalSchema));

export const ProjectDrawInfoSchema = z.object({
  drawId: z.string(),
  drawNumber: z.number(),
  fundsDisbursed: z.number(),
  fundsAvailable: z.number(),
  drawn: z.number(),
  variance: z.number(),
  fundsRequired: z.number(),
  actualVariance: z.number(),
  principalBalance: z.number(),
  committedAvailable: z.number(),
  varianceDifference: z.number(),
  equityBalance: z.number(),
  equityAvailable: z.number(),
  percentComplete: z.number(),
  percentDrawn: z.number(),
  difference: z.number(),
  estimatedVariance: z.number(),
  fundsVsBudget: z.number(),
  budget: z.number(),
  funds: z.number(),
  fundsRetained: z.number(),
  dateCreated: z.coerce.date(),
  inspectionDate: z.coerce.date().nullable(),
  completionDate: z.coerce.date().nullable(),
  dateDown: z.boolean(),
  onSchedule: z.boolean(),
  drawNotes: z.string().nullable(),
  drawStatus: z.nativeEnum(DrawStatus),
  locked: z.boolean(),
  inspectionsThisDraw: z.number().nullable().default(0),
  instructionsToInspector: z.string(),
  inspectorComments: z.string(),
  totalSiteCompleteOverride: z.boolean(),
  name: z.string(),
});

export const PortalProjectDetailSchema = z.object({
  userRole: z.nativeEnum(ProjectRole),
  roleDisplay: z.string(),
  ableToEdit: z.boolean(),
  ableToApprove: z.boolean(),
  showAttachments: z.boolean(),
  netFundsDisbursed: z.number(),
  netFundsRetained: z.number(),
  grossDraw: z.number(),
  project: ProjectSummarySchema,
  selectedDraw: SelectedDrawSchema,
  previousDrawItems: z.array(PortalDrawItemSchema),
  availableDraws: z.array(z.number()),
  currentDraw: z.number(),
});

export const ProjectSettingsSchema = z.object({
  linearNumbering: z.boolean(),
  enablePercentageBudget: z.boolean(),
  autoDisburseFunds: z.boolean(),
  enableLenderParticipation: z.boolean(),
  participationMethod: z.string(),
  enableEquityParticipation: z.boolean(),
  equityParticipationMethod: z.string(),
  enableRevolvingCredit: z.boolean(),
  hasImportedDraws: z.boolean(),
  hasImportedInspections: z.boolean(),
  budgetColumns: z.number().nullable(),
  condensedBudgetColumns: z.number().nullable(),
  enableEquityPayments: z.boolean(),
  showBorrowerName: z.boolean(),
  budgetColumnOrder: z.array(z.number()),
  sourcesColumnOrder: z.array(z.number()),
  automaticDefaultPayments: z.boolean(),
});

export const ProjectInfoSchema = z.object({
  projectId: z.string().default(""),
  projectCode: z.string().min(1, "Project ID cannot be empty"),
  projectName: z.string().min(1, "Project Name cannot be empty"),
  propertyType: CodeSchema.nullable(),
  propertyTypeEx: CodeSchema.nullable(),
  projectStatus: ProjectStatusSchema,
  checkingAccount: CheckingAccountSchema.nullable(),
  subdivision: SubdivisionSchema.nullable(),
  processor: z.object(EntitySchema.shape, { message: "Processor is required" }),
  lender: z.object(EntitySchema.shape, { message: "Lender is required" }),
  borrower: z.object(EntitySchema.shape, { message: "Borrower is required" }),
  builder: z.object(EntitySchema.shape, { message: "Builder is required" }),
  inspector: z.object(EntitySchema.shape, { message: "Inspector is required" }),
  userGroup: z.object(CodeSchema.shape, { message: "User Group is required" }),
  address: z.string(),
  county: z.string(),
  city: z.string(),
  state: z.string().nullish(),
  projectNotes: z.string(),
  specialInstructions: z.string(),
  allocatedInspections: z.number().nullable().default(0),
  defaultPayAction: z.string(),
  hasRetainage: z.boolean(),
  sendProcessorCompletionEmail: z.boolean(),
  sendLenderCompletionEmail: z.boolean(),
  sendBorrowerCompletionEmail: z.boolean(),
  sendBuilderCompletionEmail: z.boolean(),
  sendInspectorCompletionEmail: z.boolean(),
  soldDate: z.coerce.date().nullable(),
});

const ProjectZipReadSchema = z.object({
  zip: z.string(),
});

const zipUS = z.string().regex(RegExp("^[0-9]{5}(-?[0-9]{4})? *$"));
const zipCanada = z.string().regex(RegExp("^[a-zA-Z][0-9][a-zA-Z] [0-9][a-zA-Z][0-9] *$"));
const zipAustralia = z.string().regex(RegExp("^[0-9]{4}$"));

const ProjectZipWriteSchema = z.object({
  zip: z.union([zipUS, zipCanada, zipAustralia]).optional().or(z.literal("")),
});

export const ProjectInfoReadSchema = z.intersection(ProjectInfoSchema, ProjectZipReadSchema);
export const ProjectInfoWriteSchema = z.intersection(ProjectInfoSchema, ProjectZipWriteSchema);

const ProjectTotalsSchema = z.object({
  retailValue: z.number(),
  loanAmount: z.number(),
  releaseAmount: z.number(),
  budgetAmount: z.number(),
  drawnToDate: z.number(),
  fundsRequired: z.number(),
  fundsAvailable: z.number(),
  difference: z.number(),
  interestRate: z.number(),
  dateDowns: z.number(),
});

export const ProjectSchema = z.object({
  projectInfo: ProjectInfoReadSchema,
  projectSettings: ProjectSettingsSchema,
  projectTotals: ProjectTotalsSchema,
  projectDrawInfo: ProjectDrawInfoSchema,
});

export const ProjectDashboardSchema = z.object({
  project: ProjectSchema,
  drawItems: DrawItemListSchema,
  projectReminders: ProjectReminderListSchema,
  prevProjectHeader: ProjectDrawInfoSchema.nullable(),
  sourceOfFunds: ProjectEntityListSchema,
  currentFlexFields: FlexRowFieldListSchema,
  checklists: ChecklistListSchema,
  contacts: ProjectEntityListSchema,
  permissions: UserPermissionsSchema,
  activityLog: ActivityLogInfoSchema,
});

export const AgingProjectSchema = z.object({
  projectCode: z.string(),
  projectName: z.string(),
  currentDrawNumber: z.number(),
  currentDrawDateCreated: z.coerce.date().nullable(),
  daysSinceDrawCreated: z.number().nullable(),
  lastApprovedDate: z.coerce.date().nullable(),
  daysSinceLastApproved: z.number().nullable(),
  lastInspectedDate: z.coerce.date().nullable(),
  daysSinceLastInspected: z.number().nullable(),
});

export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;

export type ProjectDrawInfo = z.infer<typeof ProjectDrawInfoSchema>;
export type PortalProjectDetail = z.infer<typeof PortalProjectDetailSchema>;
export type PortalDrawItem = z.infer<typeof PortalDrawItemSchema>;
export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;
export type ProjectInfo = z.infer<typeof ProjectInfoReadSchema>;
export type Project = z.infer<typeof ProjectSchema>;
export type ProjectDashboard = z.infer<typeof ProjectDashboardSchema>;
export type AdminProjectSummary = z.infer<typeof AdminProjectSummary>;
export type AgingProject = z.infer<typeof AgingProjectSchema>;
